import type { Domain, Project } from "store/appData";
import type { DatasetImage, ImageAnnotation } from "../api";

export type Size = { width: number; height: number };
export type ImageBox = { x: number; y: number; width: number; height: number };

export interface AnnotationTarget {
  url: string;
  domainName: string;
}

export function resolveAnnotationTarget(
  image: DatasetImage,
  projects: Record<number, Project>,
  domains: Record<number, Domain>,
): AnnotationTarget | null {
  const project = projects[image.project_id];
  if (!project?.primary_domain_id) return null;
  const domain = domains[project.primary_domain_id];
  if (!domain) return null;
  const trimmed = domain.url.replace(/\/+$/, "");
  const withScheme = /^https?:\/\//.test(trimmed)
    ? trimmed
    : `https://${trimmed}`;
  return {
    url: `${withScheme}/${image.project_id}/${image.id}/annotate`,
    domainName: domain.name,
  };
}

export function fitBox(container: Size, natural: Size): ImageBox {
  if (
    !container.width ||
    !container.height ||
    !natural.width ||
    !natural.height
  ) {
    return { x: 0, y: 0, width: 0, height: 0 };
  }
  const ratio = Math.min(
    container.width / natural.width,
    container.height / natural.height
  );
  const width = natural.width * ratio;
  const height = natural.height * ratio;
  return {
    x: (container.width - width) / 2,
    y: (container.height - height) / 2,
    width,
    height,
  };
}

// Smallest annotation whose normalised [0,1] rect contains the point, or null.
export function findAnnotationAt(
  normalizedX: number,
  normalizedY: number,
  list: ImageAnnotation[]
): ImageAnnotation | null {
  let smallest: ImageAnnotation | null = null;
  for (const a of list) {
    const inside =
      normalizedX >= a.x &&
      normalizedX <= a.x + a.width &&
      normalizedY >= a.y &&
      normalizedY <= a.y + a.height;
    if (!inside) continue;
    if (!smallest || a.width * a.height < smallest.width * smallest.height) {
      smallest = a;
    }
  }
  return smallest;
}
